import React from "react";
import Box from "@mui/material/Box";
import { Trans } from "@lingui/macro";
import Image from "next/image";
import Typography from "@mui/material/Typography";
import { CardMedia } from "@mui/material";
import ProductProps from "./product-props";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CreditScoreOutlinedIcon from "@mui/icons-material/CreditScoreOutlined";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { useSession } from "next-auth/react";
import { useProductStore } from "@/components/context/product-store";

interface PreviewModalContentProps {
  messageField: string;
  name: string;
  previewUploadedFile: string;
  productCard: { id: string; src: string; name: string };
  selectedBackground: {
    image: string;
    model_id: string;
    angle: string;
    hex_start: string;
    hex_end: string;
  };
  imagePath: { current: string };
  uploadedFile: { type: string; name: string };
  product: ProductProps;
  handleClose: () => void;
}

const PreviewModalContent = ({
  messageField,
  name,
  previewUploadedFile,
  productCard,
  selectedBackground,
  imagePath,
  uploadedFile,
  product,
  handleClose,
}: PreviewModalContentProps) => {
  const { qty } = useProductStore();

  const currentTime = new Date();
  const { data: session } = useSession();
  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        width: { xs: "95%", md: "80%", lg: "65%" },
        margin: "30px auto",
        transition: "transform .3s ease-out",
        border: "1px solid rgba(0, 0, 0, .2)",
        borderRadius: "6px",
        boxShadow: "0 5px 15px rgba(0, 0, 0, .5)",
      }}
    >
      <Box
        sx={{
          padding: "15px",
          borderBottom: "1px solid #e5e5e5",
          alignItems: "center",
          textAlign: "center",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Box></Box>
        <Typography
          sx={{
            fontSize: "21px",
            color: "#000",
            fontWeight: 700,
          }}
        >
          <Trans>Preview</Trans>
        </Typography>
        <CloseOutlinedIcon
          sx={{
            fontSize: "30px",
            cursor: "pointer",
            opacity: "0.2",
            "&:hover": { opacity: "0.5" },
          }}
          onClick={handleClose}
        />
      </Box>
      <Box sx={{ padding: { xs: "15px", md: "15px 30px" } }}>
        <Box sx={{ textAlign: "center", width: "fit-content", margin: "40px auto 0" }}>
          <Image src="/logo-default.png" alt="Logo" width="200" height="30" loading="lazy" />
        </Box>
        <Typography
          sx={{ textAlign: "center", fontSize: "16px", fontWeight: 600, margin: "33px auto" }}
        >
          {name},&nbsp;
          <Trans>here is a gift for you</Trans>:
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Box
            sx={{
              margin: "0 auto",
              height: { xs: "300px", sm: "345px", md: "450px" },
              maxWidth: "450px",
              width: { xs: "100vw", sm: "345px", md: "450px" },
              display: "flex",
              alignItems: "center",
              backgroundImage: selectedBackground.image
                ? `url(${imagePath.current + selectedBackground.image})`
                : `linear-gradient(${selectedBackground.angle}, ${selectedBackground.hex_start} 0%, ${selectedBackground.hex_end} 100%)`,
              backgroundSize: "contain",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
            }}
          >
            {previewUploadedFile ? (
              uploadedFile?.type?.includes("image/") ? (
                <Box
                  sx={{
                    backgroundImage: `url(${previewUploadedFile})`,
                    height: { xs: "140px", sm: "260px" },
                    width: "362px",
                    backgroundSize: "contain",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                    margin: "0 auto",
                  }}
                ></Box>
              ) : (
                <CardMedia
                  sx={{
                    height: { xs: "140px", sm: "260px" },
                    width: { xs: "260px", md: "362px" },
                    backgroundSize: "contain",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    margin: "0 auto",
                  }}
                  component="video"
                  autoPlay
                  image={previewUploadedFile}
                />
              )
            ) : (
              <Box
                sx={{
                  backgroundImage: `url(${productCard.src})`,
                  maxWidth: "260px",
                  maxHeight: "180px",
                  height: "250px",
                  width: "362px",
                  backgroundSize: "contain",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "center",
                  margin: "0 auto",
                }}
              ></Box>
            )}
          </Box>
        </Box>
        <Box sx={{ padding: "15px 5px 0px" }}>
          <Typography sx={{ fontSize: "14px", fontWeight: 600, margin: "0", opacity: 0.6 }}>
            <Trans>Personal message</Trans>:
          </Typography>
          <Typography sx={{ fontSize: "18px", fontWeight: 600 }}>{messageField}</Typography>
          <Box sx={{ margin: "30px 0", borderTop: "1px solid #d2d2d7", width: "100%" }}> </Box>
          <Typography sx={{ fontSize: "14px", fontWeight: 600, margin: "0", opacity: 0.6 }}>
            <Trans>Offered by</Trans>:
          </Typography>
          <Typography sx={{ fontSize: "18px", fontWeight: 600 }}>
            {session?.user.first_name} {session?.user.last_name}
          </Typography>
          <Box sx={{ margin: "30px 0", borderTop: "1px solid #d2d2d7", width: "100%" }}></Box>
        </Box>
        <div className="max-v[1600px] mx-auto my-0 grid grid-cols-1 gap-6 xl:grid-cols-2">
          {Array.from({ length: qty || 0 }).map((_, index) => (
            <Box
              key={index}
              sx={{
                boxShadow: "0 5px 20px 0 rgba(0, 0, 0, 0.15);",
                borderRadius: "10px",
                padding: "15px",
                border: "1px solid #eee",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: "18px",
                      fontWeight: 600,
                      width: { xs: "75%", md: "100%" },
                      margin: "0",
                      cursor: "pointer",
                      marginBottom: "10px",

                      "&:hover": {
                        color: "#0097ab",
                      },
                    }}
                  >
                    {product?.name}
                  </Typography>
                  <Typography sx={{ fontSize: "13px", fontWeight: 400, lineHeight: "18px" }}>
                    {currentTime.toLocaleDateString()}&nbsp;{" "}
                    {currentTime.toLocaleTimeString("en-US", {
                      hour12: false,
                      hour: "numeric",
                      minute: "numeric",
                    })}
                  </Typography>
                  <Typography
                    sx={{ fontSize: "13px", fontWeight: 400, lineHeight: "18px", mb: "8px" }}
                  >
                    Nr. XXXX
                  </Typography>
                </Box>
                <Box
                  sx={{
                    backgroundImage: `url(${productCard.src})`,
                    minWidth: "100px",
                    display: "block",
                    minHeight: "67px",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    margin: "0 0 0 auto",
                  }}
                ></Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "left",
                  flexDirection: { xs: "column", md: "row" },
                  gap: "10px",
                }}
              >
                <Box
                  sx={{
                    fontSize: "15px",
                    fontWeight: "600",
                    letterSpacing: "1px",
                    minWidth: "230px",
                    borderRadius: "6px",
                    height: "50px",
                    display: "inline-block",
                    border: "2px solid #0097ab",
                    padding: "13px 12px",
                    textAlign: "center",
                  }}
                >
                  XXXXXXXXX
                  <ContentCopyIcon
                    sx={{ fontSize: "20px", float: "right", color: "#0097ab", marginLeft: "5px" }}
                  />
                </Box>
                <Box sx={{ display: "flex" }}>
                  <Box
                    sx={{
                      fontSize: "15px",
                      fontWeight: "600",
                      letterSpacing: "1px",
                      minWidth: "140px",
                      borderRadius: "6px",
                      height: "50px",
                      display: "inline-block",
                      border: "2px solid #0097ab",
                      padding: "13px 12px",
                      textAlign: "center",
                    }}
                  >
                    PIN: XXXX
                    <ContentCopyIcon
                      sx={{ fontSize: "20px", float: "right", color: "#0097ab", marginLeft: "5px" }}
                    />
                  </Box>
                </Box>
              </Box>
              <Box
                sx={{
                  cursor: "pointer",
                  margin: "10px 0 0 0",
                  width: "fit-content",
                  "&:hover h5": { color: "#0097ab" },
                }}
              >
                <QrCode2Icon
                  sx={{
                    fontSize: "50px",
                    borderRadius: "50%",
                    border: "2px solid #aaa",
                    padding: "3px",
                  }}
                />
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 400,
                    margin: "0 0 0 2px",
                    textAlign: "center",
                  }}
                >
                  Scan
                </Typography>
              </Box>
              <Box
                sx={{
                  borderTop: "1px solid #d2d2d7",
                  width: "100%",
                  padding: "15px 0",
                  marginTop: "20px",
                  lineHeight: "30px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <DownloadOutlinedIcon sx={{ fontSize: "30px", marginRight: "5px" }} />
                <Typography
                  sx={{
                    fontSize: "17px",
                    fontWeight: 500,
                    cursor: "pointer",
                    "&:hover": { color: "#0097ab" },
                  }}
                >
                  <Trans>Download</Trans>
                </Typography>
              </Box>
              <Box
                sx={{
                  borderTop: "1px solid #d2d2d7",
                  width: "100%",
                  padding: "15px 0",
                  lineHeight: "30px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <ArticleOutlinedIcon sx={{ fontSize: "30px", marginRight: "5px" }} />
                <Typography
                  sx={{
                    fontSize: "17px",
                    fontWeight: 500,
                    cursor: "pointer",
                    "&:hover": { color: "#0097ab" },
                  }}
                >
                  <Trans>Terms & Conditions</Trans>
                </Typography>
              </Box>
              <Accordion sx={{ border: "none", boxShadow: "none", "&.Mui-expanded": { m: 0 } }}>
                <AccordionSummary
                  sx={{
                    color: "#0097ab",
                    fontSize: "17px",
                    fontWeight: 500,
                    display: "inline-flex",
                    p: 0,
                  }}
                  expandIcon={<ExpandMoreIcon sx={{ color: "#0097ab" }} />}
                  aria-controls="panel1-content"
                  id="panel1-header"
                >
                  <Trans>More Options</Trans>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                  <Box
                    sx={{
                      borderTop: "1px solid #d2d2d7",
                      width: "100%",
                      padding: "15px 0",
                      lineHeight: "30px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: "30px", marginRight: "5px" }} />
                    <Typography
                      sx={{
                        fontSize: "17px",
                        fontWeight: 500,
                        cursor: "pointer",
                        "&:hover": { color: "#0097ab" },
                      }}
                    >
                      <Trans>Information</Trans>
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      borderTop: "1px solid #d2d2d7",
                      width: "100%",
                      padding: "15px 0",
                      lineHeight: "30px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <CreditScoreOutlinedIcon sx={{ fontSize: "30px", marginRight: "5px" }} />
                    <Typography
                      sx={{
                        fontSize: "17px",
                        fontWeight: 500,
                        cursor: "pointer",
                        "&:hover": { color: "#0097ab" },
                      }}
                    >
                      <Trans>Mark as redeemed</Trans>
                    </Typography>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          ))}
        </div>
      </Box>
    </Box>
  );
};

export default PreviewModalContent;
